// ============================================
// Chi tiết một góp ý + ba hành động của cửa hàng
// ============================================
// BA hành động, CỐ Ý tách rời trên UI:
//
// 1. Đổi trạng thái     -> gửi `{ status }`, BE ghi `handled_by`/`handled_at`, và
//    với RESOLVED/DISMISSED thì bắn thêm NOTIFICATION cho khách.
// 2. Trả lời khách      -> gửi `{ store_reply }`, KHÁCH ĐỌC ĐƯỢC trong app.
// 3. Ghi chú nội bộ     -> gửi `{ staff_note }`, KHÔNG ai ngoài cửa hàng thấy.
//
// ⚠️⚠️ HAI Ô TEXTAREA (2) VÀ (3) LÀ CHỖ NGUY HIỂM NHẤT CỦA MÀN NÀY. Chúng gần
// giống nhau về hình dạng và nằm cạnh nhau, nhưng một cái gửi chữ tới điện thoại
// của khách còn một cái thì không. Nhầm là gửi ghi chú nội bộ ("khách này hay
// than phiền, kệ đi") cho chính người đó đọc. Vì thế hai khối PHẢI khác nhau
// bằng NHIỀU HƠN một cái nhãn: khác màu nền, khác màu viền, có icon riêng, và
// mỗi khối tự nói thẳng ai đọc được nó. Đừng "dọn cho gọn" thành hai ô cùng
// khuôn trắng — cái tiết kiệm được vài dòng CSS trả giá bằng một sự cố với khách.
//
// ⚠️ CHỈ `RESOLVED` và `DISMISSED` mới báo cho khách (BE xác nhận 28/08/2026);
// `NEW` và `IN_REVIEW` im lặng. Đầy đủ lý do + `collapse_key` ở docblock của
// `StatusChangeConfirmModal`; nguồn sự thật duy nhất là
// `statusNotifiesCustomer` trong `feedbackText.ts` — đừng so sánh chuỗi tại chỗ.
//
// ⚠️ `store_reply` theo quy tắc thông báo KHÁC HẲN `status`: chỉ lần gửi ĐẦU mới
// bắn, sửa và rút đều im lặng, nhưng rút HẲN rồi viết lại thì bắn lần nữa. Rút
// gọn thành đúng một phép kiểm: `record.store_replied_at == null` NGAY TRƯỚC khi
// gửi ⇒ lần này sẽ bắn. Đó là lý do component phải đọc giá trị đó TRƯỚC rồi
// truyền `replyWasEmpty` xuống hook — sau khi ghi thì response luôn có
// `store_replied_at`, đọc lúc đó là luôn ra "không bắn".
//
// ⚠️ Gửi kèm `status` bằng đúng giá trị CŨ vẫn tính là một lần đổi trạng thái ở
// phía BE: `handled_by`/`handled_at` bị ghi đè sang người vừa bấm, và nếu giá
// trị đó là RESOLVED/DISMISSED thì có thêm một thông báo bay tới khách. Nên nút
// "Lưu ghi chú" tuyệt đối không được đính `status` vào payload "cho chắc".
//
// Ngữ nghĩa `staff_note` VÀ `store_reply` (contract BE, giống hệt nhau):
//   không truyền khoá -> giữ nguyên
//   `''`              -> nội dung rỗng
//   `null`            -> XOÁ / RÚT LẠI
// Ba giá trị, ba ý nghĩa khác nhau — UI phải có đủ ba đường đi tới chúng.
//
// Toast thành công/lỗi đã nằm trong `useUpdateStaffFeedback`. KHÔNG toast lại ở
// đây, nếu không mỗi lần lưu sẽ hiện hai thông báo chồng nhau.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { FeedbackStatus, StaffFeedback, UUID } from '@conai/shared-types';
import { StatusBadge } from '../../../components/shared';
import { AlertTriangleIcon, BellIcon, FeedbackIcon, XIcon } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { useStaffFeedbackDetail, useUpdateStaffFeedback } from '../../../hooks/useFeedback';
import {
  EMPTY_VALUE,
  FEEDBACK_STATUS_ORDER,
  FEEDBACK_STATUS_VARIANT,
  FEEDBACK_TEXT_MAX_LENGTH,
  shortId,
  useFeedbackText,
} from '../feedbackText';
import { FeedbackPhotoGrid } from './FeedbackPhotoGrid';
import { RatingStars } from './RatingStars';
import { ReplyConfirmModal } from './ReplyConfirmModal';
import { StatusChangeConfirmModal } from './StatusChangeConfirmModal';

/** Thao tác đang chờ server — bốn nút dùng CHUNG một mutation nên phải phân biệt. */
type PendingAction = 'status' | 'save-note' | 'clear-note' | 'save-reply' | 'withdraw-reply' | null;

/** Hộp xác nhận nào đang mở cho ô trả lời. `null` = không có. */
type ReplyConfirmMode = 'send' | 'withdraw' | null;

/** Còn dưới ngần này ký tự thì bộ đếm chuyển màu — cảnh báo trước khi chạm trần. */
const COUNTER_WARN_REMAINING = 100;

interface FeedbackDetailModalProps {
  feedbackId: UUID;
  /**
   * Dòng đang có sẵn trong bảng. Chi tiết và danh sách CÙNG shape, nên hiện tạm
   * dòng này trong lúc `GET /feedback/staff/{id}/` chạy — không có nó thì mỗi
   * lần mở modal là một khoảng trắng, dù dữ liệu đã nằm sẵn trên màn hình.
   */
  fallbackRecord?: StaffFeedback;
  onClose: () => void;
}

/**
 * Mắt bị gạch — dấu "không ai ngoài cửa hàng nhìn thấy" cho khối ghi chú nội bộ.
 *
 * Khai tại chỗ (như `PhotoIcon` trong `FeedbackTable`) vì `components/icons` chỉ
 * có `EyeIcon`, mà mắt MỞ ở đây là tín hiệu ngược hẳn với ý cần nói.
 */
function EyeOffIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7.2 4.6A7.6 7.6 0 0 1 10 4.2c4 0 7 3.3 7.5 5.8-.2.9-.8 2-1.8 3" />
      <path d="M4.6 6.3C3.2 7.4 2.4 8.8 2.2 10c.5 2.5 3.5 5.8 7.5 5.8 1.2 0 2.3-.3 3.2-.8" />
      <path d="M8.4 8.6a2.2 2.2 0 0 0 3 3" />
      <path d="M3.5 3.5l13 13" />
    </svg>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">{label}</p>
      <div className="mt-1 text-sm text-slate-900">{children}</div>
    </div>
  );
}

export function FeedbackDetailModal({ feedbackId, fallbackRecord, onClose }: FeedbackDetailModalProps) {
  const { t, tr, statusLabel, typeLabel, issueKindLabel, formatDateTime } = useFeedbackText();

  const detail = useStaffFeedbackDetail(feedbackId);
  const update = useUpdateStaffFeedback();

  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [nextStatus, setNextStatus] = useState<FeedbackStatus | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [replyConfirm, setReplyConfirm] = useState<ReplyConfirmMode>(null);

  const record = detail.data ?? fallbackRecord;
  const savedNote = record?.staff_note ?? '';
  const noteDirty = noteDraft !== savedNote;

  const savedReply = record?.store_reply ?? '';
  const replyDirty = replyDraft !== savedReply;

  /**
   * Lần ghi tới CÓ bắn thông báo cho khách không.
   *
   * `store_replied_at == null` (dùng `==` để bắt cả `undefined` của bản ghi cũ
   * chưa có field, trong lúc BE chưa deploy) = chưa từng trả lời, HOẶC vừa rút
   * lại. Cả hai đều được BE tính là "lần đầu" và đều bắn thông báo — xem
   * docblock đầu file. Đọc từ `record`, tức TRƯỚC request; sau request thì
   * response luôn có mốc thời gian và câu trả lời sẽ luôn là "không bắn".
   */
  const replyWillNotify = record?.store_replied_at == null;
  /**
   * Có câu trả lời đang hiển thị cho khách -> mới có gì để mà rút lại.
   * `!= null` cố ý bắt cả `undefined` (bản ghi từ BE chưa deploy field này).
   */
  const hasPublishedReply = record?.store_reply != null;

  const isBusy = update.isPending;

  useLockBodyScroll(true);

  // Đồng bộ ô ghi chú theo dữ liệu server. Deps là GIÁ TRỊ chuỗi, không phải
  // object, nên một lần refetch trả về đúng ghi chú cũ sẽ KHÔNG xoá những gì
  // người dùng đang gõ dở.
  useEffect(() => {
    setNoteDraft(savedNote);
  }, [feedbackId, savedNote]);

  // Cùng lý do, cho ô trả lời. Hai effect riêng chứ không gộp: gộp thì một lần
  // lưu ghi chú (đổi `savedNote`) sẽ đè luôn câu trả lời đang soạn dở.
  useEffect(() => {
    setReplyDraft(savedReply);
  }, [feedbackId, savedReply]);

  const handleClose = useCallback(() => {
    if (isBusy) return;
    onClose();
  }, [isBusy, onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Esc đóng lớp trên cùng trước: ảnh phóng to -> hộp xác nhận -> modal.
      if (zoomUrl) {
        setZoomUrl(null);
        return;
      }
      if (nextStatus) {
        if (!isBusy) setNextStatus(null);
        return;
      }
      if (replyConfirm) {
        if (!isBusy) setReplyConfirm(null);
        return;
      }
      handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [zoomUrl, nextStatus, replyConfirm, isBusy, handleClose]);

  /** Đổi trạng thái — payload CHỈ có `status`. */
  const confirmStatusChange = () => {
    if (!record || !nextStatus) return;
    setPendingAction('status');
    update.mutate(
      { id: record.id, data: { status: nextStatus } },
      {
        onSuccess: () => setNextStatus(null),
        onSettled: () => setPendingAction(null),
      }
    );
  };

  /** Lưu ghi chú — payload CHỈ có `staff_note`, KHÔNG kèm `status`. */
  const handleSaveNote = () => {
    if (!record || !noteDirty) return;
    setPendingAction('save-note');
    update.mutate(
      // `noteDraft` có thể là `''`: đó là "ghi chú rỗng", khác với "xoá ghi chú"
      // (`null`) và khác với "không đụng tới" (không truyền khoá).
      { id: record.id, data: { staff_note: noteDraft } },
      { onSettled: () => setPendingAction(null) }
    );
  };

  /** Xoá hẳn ghi chú — `null` chứ không phải chuỗi rỗng. */
  const handleClearNote = () => {
    if (!record) return;
    setPendingAction('clear-note');
    update.mutate(
      { id: record.id, data: { staff_note: null } },
      { onSettled: () => setPendingAction(null) }
    );
  };

  /**
   * Ghi câu trả lời — payload CHỈ có `store_reply`.
   *
   * `replyWasEmpty` được chốt Ở ĐÂY, từ `record` (dữ liệu TRƯỚC request). Hook
   * dùng nó để chọn câu toast "đã báo cho khách" hay "không báo lại" — nó không
   * suy được điều đó từ response.
   */
  const submitReply = () => {
    if (!record || !replyDirty) return;
    setPendingAction('save-reply');
    update.mutate(
      { id: record.id, data: { store_reply: replyDraft }, replyWasEmpty: replyWillNotify },
      {
        onSuccess: () => setReplyConfirm(null),
        onSettled: () => setPendingAction(null),
      }
    );
  };

  /**
   * Bấm nút gửi/lưu câu trả lời.
   *
   * Xác nhận CHỈ cho lần đầu (lần có thông báo). Lần sửa đi thẳng: BE im lặng,
   * bắt xác nhận cho một thao tác vô hình chỉ làm người dùng bấm Confirm theo
   * phản xạ — rồi đúng lúc hộp đó quan trọng thì không ai đọc nữa.
   */
  const handleReplyButton = () => {
    if (!record || !replyDirty) return;
    if (replyWillNotify) {
      setReplyConfirm('send');
      return;
    }
    submitReply();
  };

  /** Rút lại câu trả lời — `null`, KHÔNG phải chuỗi rỗng (`''` vẫn là một câu trả lời). */
  const confirmWithdrawReply = () => {
    if (!record) return;
    setPendingAction('withdraw-reply');
    update.mutate(
      // `replyWasEmpty: false` — rút lại KHÔNG bao giờ bắn thông báo. Truyền
      // tường minh thay vì để `undefined` cho khỏi phải đoán ở đầu bên kia.
      { id: record.id, data: { store_reply: null }, replyWasEmpty: false },
      {
        onSuccess: () => setReplyConfirm(null),
        onSettled: () => setPendingAction(null),
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className="my-auto w-full max-w-3xl rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900">
              {tr('feedbackDetailTitle', 'Feedback details')}
            </h2>
            {/* `customer_name` KHÔNG BAO GIỜ null (BE 28/08/2026: FK
                `on_delete=CASCADE`, không nullable; tên lấy từ `full_name` bắt
                buộc). Không có nhánh "tài khoản đã xoá" — chỉ cần chờ `record`
                về, và trong lúc chờ thì không vẽ dòng trống. */}
            {record ? (
              <p className="truncate text-sm text-slate-500">{record.customer_name}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isBusy}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40"
            aria-label={t('close')}
          >
            <XIcon size={20} />
          </button>
        </div>

        {!record ? (
          <div className="px-6 py-12 text-center">
            {detail.isError ? (
              <>
                <p className="text-sm text-slate-500">
                  {tr('feedbackDetailLoadError', 'Could not load this feedback.')}
                </p>
                <button
                  type="button"
                  onClick={() => detail.refetch()}
                  className="mt-3 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {tr('feedbackRetry', 'Try again')}
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('loading')}</p>
            )}
          </div>
        ) : (
          <div className="max-h-[75vh] space-y-6 overflow-y-auto px-6 py-5">
            {/* ⚠️ NỘI DUNG ĐÃ ĐỔI SAU KHI CỬA HÀNG XỬ LÝ.
                Banner này đứng ĐẦU, trên cả phần tóm tắt: nó đổi nghĩa của mọi
                thứ bên dưới. Câu trả lời và ghi chú đang hiện là viết cho một
                nội dung KHÁC — BE cố ý GIỮ chúng (đã bỏ hành vi xoá `staff_note`
                trước đây) vì mất chúng là mất dấu vết vì sao cửa hàng từng quyết
                định như vậy. Đọc lại rồi hãy đóng lần nữa. */}
            {record.staff_content_stale ? (
              <div className="flex items-start gap-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                <span className="mt-0.5 shrink-0 text-amber-600">
                  <AlertTriangleIcon size={20} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-amber-900">
                    {tr('feedbackStaleTitle', 'The customer edited this feedback after it was handled')}
                  </p>
                  <p className="mt-1 text-sm text-amber-800">
                    {tr(
                      'feedbackStaleBody',
                      'What you read above is the new version. Any reply and internal note below were written for the old one and may no longer match it. The status was reset to New.'
                    )}
                  </p>
                  {/* Nói thẳng cách tắt cờ. Không có câu này thì người dùng sẽ
                      bấm một nút trạng thái, thấy banner vẫn còn, rồi tưởng hỏng. */}
                  <p className="mt-1 text-xs text-amber-700">
                    {tr(
                      'feedbackStaleClearHint',
                      'This warning disappears once you write a reply or an internal note. Changing the status alone does not clear it.'
                    )}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Tóm tắt */}
            <div className="grid grid-cols-2 gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-4">
              <Field label={tr('feedbackColumnDate', 'Received')}>
                {formatDateTime(record.created_at)}
              </Field>
              <Field label={t('type')}>
                <p>{typeLabel(record.feedback_type)}</p>
                {record.issue_kind ? (
                  <p className="text-xs text-slate-500">{issueKindLabel(record.issue_kind)}</p>
                ) : null}
              </Field>
              <Field label={tr('feedbackRating', 'Rating')}>
                {/* `rating: null` = khách chỉ viết chữ/gửi ảnh, KHÔNG phải 0 sao. */}
                <RatingStars rating={record.rating} showValue />
              </Field>
              <Field label={t('status')}>
                <StatusBadge variant={FEEDBACK_STATUS_VARIANT[record.status] ?? 'neutral'}>
                  {statusLabel(record.status)}
                </StatusBadge>
              </Field>
            </div>

            {/* Nội dung khách viết */}
            <section>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">
                {tr('feedbackCustomerMessage', 'What the customer wrote')}
              </p>
              {record.comment ? (
                <p className="whitespace-pre-wrap break-words rounded-xl border border-slate-200 p-4 text-sm leading-relaxed text-slate-700">
                  {record.comment}
                </p>
              ) : (
                <p className="text-sm italic text-slate-400">
                  {tr('feedbackNoComment', 'No comment')}
                </p>
              )}
            </section>

            {/* Ảnh */}
            <section>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">
                {tr('feedbackColumnPhotos', 'Photos')}
                {record.photo_urls.length > 0 ? ` (${record.photo_urls.length})` : ''}
              </p>
              <FeedbackPhotoGrid photoUrls={record.photo_urls} onZoom={setZoomUrl} />
            </section>

            {/* Ngữ cảnh. ĐÚNG BA FK này (`store`/`product`/`order`) là
                `on_delete=SET_NULL` + nullable, nên id và tên có thể cùng
                `null` — ba fallback dưới đây PHẢI giữ. (`customer` thì không:
                nó CASCADE và không nullable.) */}
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label={tr('feedbackColumnStore', 'Store')}>
                {record.store_name || (
                  <span className="italic text-slate-400">
                    {record.store
                      ? tr('feedbackUnnamedReference', 'Name unavailable')
                      : EMPTY_VALUE}
                  </span>
                )}
              </Field>
              <Field label={t('product')}>
                {record.product_name || (
                  <span className="italic text-slate-400">
                    {record.product
                      ? tr('feedbackUnnamedReference', 'Name unavailable')
                      : EMPTY_VALUE}
                  </span>
                )}
              </Field>
              <Field label={tr('feedbackRelatedOrder', 'Order')}>
                {/* `order` KHÔNG có `order_name` đi kèm — chỉ có UUID. */}
                {record.order ? (
                  <span className="font-mono text-xs" title={record.order}>
                    {shortId(record.order)}
                  </span>
                ) : (
                  <span className="text-slate-400">{EMPTY_VALUE}</span>
                )}
              </Field>
            </section>

            {/* ══════════════════════════════════════════════════════════════
                TRẢ LỜI KHÁCH — chữ ở ô này BAY TỚI ĐIỆN THOẠI CỦA KHÁCH.
                Khối này và khối "Ghi chú nội bộ" ngay bên dưới cố ý KHÔNG giống
                nhau: nền xanh + viền dày + icon bong bóng ở đây, nền xám + viền
                đứt nét + chip "chỉ nhân viên" ở kia. Nhãn thôi là không đủ —
                người xử lý hàng chục góp ý một buổi không đọc nhãn, họ nhận ra
                khối bằng màu. Ai định gộp hai khối về cùng một khuôn: đọc
                docblock đầu file trước.
                ══════════════════════════════════════════════════════════════ */}
            <section className="rounded-xl border-2 border-primary-200 bg-primary-50/50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <span className="text-primary-600">
                    <FeedbackIcon size={18} />
                  </span>
                  {tr('feedbackStoreReply', 'Reply to customer')}
                </p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700">
                  <BellIcon size={13} />
                  {tr('feedbackStoreReplyVisibleBadge', 'The customer reads this')}
                </span>
              </div>

              <p className="mt-2 text-xs text-slate-600">
                {tr(
                  'feedbackStoreReplyHint',
                  'This text appears in the customer app under their feedback. Write it as if you were speaking to them — it is not an internal note.'
                )}
              </p>

              {/* Câu trả lời ĐANG hiển thị cho khách + ai viết, lúc nào. Hiện
                  riêng phía trên ô nhập: ô nhập là "sắp gửi", khối này là "khách
                  đang đọc gì lúc này". Hai thứ khác nhau ngay khi bắt đầu gõ. */}
              {hasPublishedReply ? (
                <div className="mt-3 rounded-lg border border-primary-200 bg-white px-3 py-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">
                    {tr('feedbackStoreReplyPublished', 'Currently visible to the customer')}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-700">
                    {record.store_reply}
                  </p>
                  <p className="mt-1.5 text-xs text-slate-400">
                    {/* `store_replied_by_name` CHỈ có ở serializer staff. Nó
                        nullable — bản ghi cũ, hoặc tài khoản nhân viên đã gỡ. */}
                    {record.store_replied_by_name || tr('feedbackStoreReplyByUnknown', 'Store staff')}
                    {record.store_replied_at ? ` · ${formatDateTime(record.store_replied_at)}` : ''}
                  </p>
                </div>
              ) : null}

              <textarea
                value={replyDraft}
                onChange={(event) => setReplyDraft(event.target.value)}
                rows={4}
                // Chặn Ở ĐÂY chứ không chỉ ở nút: BE trả 400 khi vượt 2000, và
                // một cái 400 sau khi đã gõ xong là mất chữ + mất thời gian.
                maxLength={FEEDBACK_TEXT_MAX_LENGTH}
                placeholder={tr(
                  'feedbackStoreReplyPlaceholder',
                  'Thank the customer and tell them what you are doing about it…'
                )}
                className="mt-3 w-full rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary-500"
              />

              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <p
                    className={cn(
                      'text-xs tabular-nums',
                      replyDraft.length > FEEDBACK_TEXT_MAX_LENGTH - COUNTER_WARN_REMAINING
                        ? 'font-semibold text-amber-600'
                        : 'text-slate-400'
                    )}
                  >
                    {replyDraft.length}/{FEEDBACK_TEXT_MAX_LENGTH}
                  </p>
                  {/* Xoá sạch ô rồi bấm Lưu sẽ gửi `''` — vẫn là "một câu trả
                      lời", chỉ là câu rỗng, và khách vẫn thấy một khối trả lời
                      trống. Muốn gỡ hẳn thì phải là `null`. Nút Gửi bị khoá ở ca
                      này, nên phải nói rõ vì sao. */}
                  {replyDirty && !replyDraft.trim() ? (
                    <p className="text-xs text-amber-600">
                      {tr(
                        'feedbackStoreReplyEmptyHint',
                        'An empty reply is still a reply. Use “Withdraw reply” to remove it from the customer app.'
                      )}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {/* "Rút lại" gửi `null`; chỉ có nghĩa khi server đang giữ một
                      câu trả lời. */}
                  {hasPublishedReply ? (
                    <button
                      type="button"
                      onClick={() => setReplyConfirm('withdraw')}
                      disabled={isBusy}
                      className="rounded-lg px-3 py-2 text-sm font-semibold text-[#EF4444] transition-colors hover:bg-red-50 disabled:opacity-40"
                    >
                      {pendingAction === 'withdraw-reply' && isBusy
                        ? t('processing')
                        : tr('feedbackWithdrawReply', 'Withdraw reply')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleReplyButton}
                    // Không đổi gì thì KHÔNG gửi request: "không truyền khoá" là
                    // cách duy nhất nói với BE rằng câu trả lời giữ nguyên. Và
                    // gửi lại đúng câu cũ khi vừa rút xong là một thông báo nữa
                    // cho khách.
                    disabled={isBusy || !replyDirty || !replyDraft.trim()}
                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-40"
                  >
                    {pendingAction === 'save-reply' && isBusy
                      ? t('savingBtn')
                      : replyWillNotify
                      ? tr('feedbackSendReply', 'Send reply')
                      : tr('feedbackUpdateReply', 'Update reply')}
                  </button>
                </div>
              </div>

              {/* Nói TRƯỚC khi bấm chuyện gì sẽ xảy ra, chứ không chỉ trong hộp
                  xác nhận: người dùng quyết định viết gì dựa trên việc câu này có
                  đẩy thông báo hay không. */}
              <p className="mt-2 text-xs text-slate-500">
                {replyWillNotify
                  ? tr(
                      'feedbackReplyWillNotify',
                      'Sending your first reply notifies the customer on their phone.'
                    )
                  : tr(
                      'feedbackReplyWillNotNotify',
                      'You already replied, so editing it updates the text silently — no new notification.'
                    )}
              </p>
            </section>

            {/* Ghi chú nội bộ. Viền ĐỨT NÉT + nền xám + chip "chỉ nhân viên" —
                cố ý trông "chưa gửi đi đâu cả", ngược hẳn khối trả lời phía trên. */}
            <section className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">
                  {tr('feedbackStaffNote', 'Internal note')}
                </p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  <EyeOffIcon size={13} />
                  {tr('feedbackStaffNoteBadge', 'The customer never sees this')}
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {tr('feedbackStaffNoteHint', 'Only visible to store staff. Saving does not notify the customer.')}
              </p>

              <textarea
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                rows={3}
                maxLength={FEEDBACK_TEXT_MAX_LENGTH}
                placeholder={tr('feedbackStaffNotePlaceholder', 'What did you do about this?')}
                className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary-500"
              />

              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                {/* "Xoá ghi chú" gửi `null`; nó chỉ có nghĩa khi server đang giữ
                    một ghi chú. Ô nhập trống + bấm Lưu là gửi `''` — vẫn là một
                    ghi chú, chỉ là ghi chú rỗng. */}
                {record.staff_note !== null ? (
                  <button
                    type="button"
                    onClick={handleClearNote}
                    disabled={isBusy}
                    className="rounded-lg px-3 py-2 text-sm font-semibold text-[#EF4444] transition-colors hover:bg-red-50 disabled:opacity-40"
                  >
                    {pendingAction === 'clear-note' && isBusy
                      ? t('processing')
                      : tr('feedbackClearNote', 'Delete note')}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleSaveNote}
                  // Không đổi gì thì KHÔNG gửi request: "không truyền khoá" là
                  // cách duy nhất nói với BE rằng ghi chú giữ nguyên.
                  disabled={isBusy || !noteDirty}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-40"
                >
                  {pendingAction === 'save-note' && isBusy
                    ? t('savingBtn')
                    : tr('feedbackSaveNote', 'Save note')}
                </button>
              </div>
            </section>

            {/* Người xử lý */}
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={tr('feedbackHandledBy', 'Handled by')}>
                {record.handled_by_name || (
                  <span className="text-slate-400">
                    {tr('feedbackNotHandledYet', 'Nobody yet')}
                  </span>
                )}
              </Field>
              <Field label={tr('feedbackHandledAt', 'Handled at')}>
                {record.handled_at ? formatDateTime(record.handled_at) : (
                  <span className="text-slate-400">{EMPTY_VALUE}</span>
                )}
              </Field>
            </section>

            {/* Đổi trạng thái */}
            <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <p className="text-sm font-semibold text-slate-900">
                {tr('feedbackChangeStatus', 'Change status')}
              </p>
              {/* KEY MỚI. `feedbackChangeStatusWarning` ("Every status change
                  sends a notification…") là một lời khẳng định SAI SỰ THẬT — chỉ
                  Resolved và Dismissed mới báo cho khách. Không sửa nội dung key
                  cũ tại chỗ vì bản dịch pl/uk của nó cũng đang nói điều sai đó. */}
              <p className="mt-1 text-xs text-amber-700">
                {tr(
                  'feedbackChangeStatusNotifyHint',
                  'Resolved and Dismissed notify the customer. New and In review are silent — they only record who is handling this.'
                )}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {FEEDBACK_STATUS_ORDER.map((status) => {
                  const isCurrent = status === record.status;
                  return (
                    <button
                      key={status}
                      type="button"
                      // Trạng thái hiện tại bị khoá: gửi lại đúng giá trị cũ
                      // KHÔNG phải thao tác vô hại. BE vẫn coi đó là một lần đổi
                      // — ghi đè `handled_by`/`handled_at`, và với
                      // RESOLVED/DISMISSED thì gửi cho khách thêm một thông báo
                      // nữa về một việc chẳng có gì mới.
                      disabled={isCurrent || isBusy}
                      onClick={() => setNextStatus(status)}
                      className={cn(
                        'rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                        isCurrent
                          ? 'cursor-default border-slate-200 bg-white text-slate-400'
                          : status === 'DISMISSED'
                          ? 'border-red-200 bg-white text-[#EF4444] hover:bg-red-50 disabled:opacity-40'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-white/80 hover:border-primary-400 disabled:opacity-40'
                      )}
                    >
                      {statusLabel(status)}
                      {isCurrent ? (
                        <span className="ml-1.5 text-xs font-normal">
                          ({tr('feedbackCurrentStatus', 'current')})
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Xác nhận đổi trạng thái */}
      {record && nextStatus ? (
        <StatusChangeConfirmModal
          currentStatus={record.status}
          nextStatus={nextStatus}
          isPending={pendingAction === 'status' && isBusy}
          hasUnsavedNote={noteDirty}
          // Một PATCH `{ status }` KHÔNG mang theo câu trả lời đang gõ dở — và ở
          // đây hậu quả nặng hơn ghi chú: người dùng tưởng "trả lời xong, đóng
          // luôn" là một thao tác, trong khi khách không nhận được chữ nào.
          hasUnsavedReply={replyDirty}
          onConfirm={confirmStatusChange}
          onCancel={() => setNextStatus(null)}
        />
      ) : null}

      {/* Xác nhận gửi / rút lại câu trả lời */}
      {record && replyConfirm ? (
        <ReplyConfirmModal
          mode={replyConfirm}
          replyText={replyDraft}
          isPending={
            (pendingAction === 'save-reply' || pendingAction === 'withdraw-reply') && isBusy
          }
          hasUnsavedNote={noteDirty}
          onConfirm={replyConfirm === 'send' ? submitReply : confirmWithdrawReply}
          onCancel={() => {
            if (!isBusy) setReplyConfirm(null);
          }}
        />
      ) : null}

      {/* Ảnh phóng to */}
      {zoomUrl ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-6"
          // `stopPropagation` BẮT BUỘC: lớp phóng to nằm BÊN TRONG overlay của
          // modal chi tiết, mà overlay đó có `onClick={handleClose}`. Thiếu dòng
          // này thì một cú bấm để đóng ảnh sẽ đóng luôn cả modal chi tiết.
          onClick={(event) => {
            event.stopPropagation();
            setZoomUrl(null);
          }}
        >
          <img
            src={zoomUrl}
            alt={tr('feedbackPhotoZoomAlt', 'Customer photo')}
            className="max-h-full max-w-full object-contain"
            // URL ảnh là link tĩnh vĩnh viễn dưới `/media/` của Django, KHÔNG
            // hết hạn (xem `FeedbackPhotoGrid`). Nhưng ảnh vẫn hỏng được vì lý
            // do khác (file bị xoá, đổi domain, mạng chập chờn) — hỏng thì đóng
            // lớp phóng to lại, để một ô đen trống giữa màn hình thì người dùng
            // chỉ biết bấm lung tung.
            onError={() => setZoomUrl(null)}
          />
        </div>
      ) : null}
    </div>
  );
}
